import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import sortBy from 'lodash.sortby';
import { ROLE, ACCESS_LEVEL } from '@constants/user';
import { useLocations } from './useLocations';

export const useUsersOptions = () => {
    const locations = useSelector(state => state.account?.account?.companies || []);
    const users = useSelector(state => state.account?.account?.users || []);
    const { locationsMap } = useLocations();

    const mapUserPropsToOption = user => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
    });

    const getUsersOptionsByLocationId = useCallback(
        (locationId, propsMapper = mapUserPropsToOption) => {
            return sortBy(
                users
                    .filter(user => {
                        if (user.role === ROLE.SUPER_ADMIN || user?.accessLevel === ACCESS_LEVEL.GLOBAL) return true;
                        if (user?.accessLevel === ACCESS_LEVEL.BRAND) {
                            const brand = locationsMap[locationId]?.brand;
                            return brand?.id ? user.brands.includes(brand?.id) : false;
                        }
                        if (user?.accessLevel === ACCESS_LEVEL.LOCATION) {
                            return user.locations?.some(locationId => locationId === locationId);
                        }
                        return false;
                    })
                    .map(propsMapper),
                'label',
            );
        },
        [users, locationsMap],
    );

    const getUsersOptionsByLocationIds = useCallback(
        (locationIds, propsMapper = mapUserPropsToOption) => {
            return sortBy(
                users
                    .filter(user => {
                        if (user.role === ROLE.SUPER_ADMIN || user?.accessLevel === ACCESS_LEVEL.GLOBAL) return true;
                        if (user?.accessLevel === ACCESS_LEVEL.BRAND) {
                            const brands = locations
                                .filter(location => locationIds.includes(location.companyId))
                                .map(location => location.brand?.id)
                                .filter(Boolean);
                            return brands.some(brandId => user.brands.includes(brandId));
                        }
                        if (user?.accessLevel === ACCESS_LEVEL.LOCATION) {
                            return user.locations?.some(locationId => locationIds.includes(locationId));
                        }
                        return false;
                    })
                    .map(propsMapper),
                'label',
            );
        },
        [getUsersOptionsByLocationId],
    );

    return {
        getUsersOptionsByLocationId,
        getUsersOptionsByLocationIds,
    };
};
